import React, { useEffect, useRef, useState } from "react";
import { detectTextDirection } from "../utils/textDirection";
import { normalizeMetadata, hasMetadataContent } from "../utils/metadata";

// Section A: public. The "شناسه" (colophon/identity) button on a content
// page (ArticleDetailPage.js, TopicDetailPage.js, IssueTopicDetailPage.js)
// opens this as a centered modal overlay — distinct from Footnotes.js's
// corner-popover پاورقی button, since this is a one-off reference block
// rather than something read alongside the body while scrolling.
//
// Always renders, even with no metadata set yet, same reasoning as
// Footnotes.js: the affordance stays consistent across every content page
// rather than appearing/disappearing based on whether an admin has
// filled it in yet.
function MetadataModal({ metadata: rawMetadata }) {
  const metadata = normalizeMetadata(rawMetadata);
  const hasContent = hasMetadataContent(metadata);
  const [open, setOpen] = useState(false);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    function handlePointerDown(event) {
      if (dialogRef.current && !dialogRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const extraFields = metadata.extra_fields.filter((field) => field.label || field.value);

  return (
    <>
      <button type="button" className="metadata-trigger" onClick={() => setOpen(true)}>
        شناسه
      </button>

      {open && (
        <div className="metadata-modal-backdrop">
          <div
            ref={dialogRef}
            className="metadata-modal"
            role="dialog"
            aria-modal="true"
            aria-label="شناسه"
          >
            <div className="metadata-modal-header">
              <span className="metadata-modal-title">شناسه</span>
              <button
                type="button"
                className="metadata-modal-close"
                onClick={() => setOpen(false)}
                aria-label="بستن"
              >
                ×
              </button>
            </div>

            {hasContent ? (
              <dl className="metadata-modal-list">
                {metadata.english_author_name && (
                  <div className="metadata-modal-row">
                    <dt>نام نویسنده</dt>
                    <dd dir="ltr">{metadata.english_author_name}</dd>
                  </div>
                )}

                {metadata.text_id && (
                  <div className="metadata-modal-row">
                    <dt>شناسهٔ متن</dt>
                    <dd dir={detectTextDirection(metadata.text_id)}>{metadata.text_id}</dd>
                  </div>
                )}

                {metadata.translator_name && (
                  <div className="metadata-modal-row">
                    <dt>مترجم</dt>
                    <dd dir={detectTextDirection(metadata.translator_name)}>{metadata.translator_name}</dd>
                  </div>
                )}

                {metadata.author_bio && (
                  <div className="metadata-modal-row">
                    <dt>بیوگرافی نویسنده</dt>
                    <dd dir={detectTextDirection(metadata.author_bio)}>{metadata.author_bio}</dd>
                  </div>
                )}

                {extraFields.map((field, index) => (
                  <div className="metadata-modal-row" key={index}>
                    <dt dir={detectTextDirection(field.label)}>{field.label}</dt>
                    <dd dir={detectTextDirection(field.value)}>{field.value}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="metadata-modal-empty">شناسه‌ای برای این صفحه ثبت نشده است.</p>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default MetadataModal;
